/**
 * Configuración del bundle Certificado + Plan.
 *
 * Se venden los DOS productos juntos: se cobra el certificado más el plan, en
 * un único cargo a la tarjeta. El atractivo es hacer un solo trámite y un solo
 * pago para quedar operativo, no que el plan sea gratis.
 *
 * Nada de esto se hardcodea ni se duplica en variables de entorno:
 *
 *  · Qué plan ofrece el bundle → lo declara el catálogo en `limits.bundle`
 *  · Con qué diseño se muestra  → `limits.promo`
 *  · Cuánto cuesta              → la suma de ambos precios, leídos del catálogo
 *
 * Lo único configurable es QUÉ plan es el del certificado, porque vive en otro
 * producto (`fe-crt`) y no viene en la lista de planes de Factura Libre.
 */

import type { PlanLimits } from '../types/subscription.types';

/**
 * Plan del certificado en el producto "Certificados FE".
 * Mismo ID en QA y producción; es también el que usa el flujo público.
 */
export const BUNDLE_CERTIFICATE_PLAN_ID = (
  import.meta.env.VITE_BUNDLE_CERTIFICATE_PLAN_ID || ''
).trim();

/** ¿Este plan ofrece el bundle con certificado? */
export function isBundleEligiblePlan(plan: { limits?: PlanLimits | null }): boolean {
  return plan.limits?.bundle === true;
}

/**
 * Precio de campaña del plan, o `null` si no está en promoción.
 *
 * Sirve para las dos formas en que llega un plan, que traen el precio en campos
 * distintos:
 *
 *  · desde el portal (`/companies/available-plans`), el backend ya sustituyó el
 *    precio: `price` es el de campaña y `regular_price` el de lista;
 *  · desde el catálogo público, sin pasar por el backend: `price` es el de lista
 *    y el de campaña está en `limits.descuento`.
 */
export function precioPromocional(plan: {
  price?: number;
  regular_price?: number;
  limits?: PlanLimits | null;
}): number | null {
  if (plan.regular_price != null && plan.price != null && plan.regular_price > plan.price) {
    return plan.price;
  }

  const declarado = plan.limits?.descuento;
  if (declarado === undefined || declarado === null || declarado === '') return null;

  const promocional = Number(declarado);
  if (!Number.isFinite(promocional) || promocional <= 0) return null;

  // Un descuento que no baja el precio es un error de configuración: el backend
  // lo ignora y lo registra, así que acá tampoco se trata como promoción.
  if (plan.price != null && promocional >= plan.price) return null;

  return promocional;
}

/**
 * ¿Este plan se muestra con tratamiento de promoción?
 *
 * Basta con declarar el precio de campaña: no hace falta acordarse de poner
 * también `promo`, que se mantiene para encender el realce por sí solo.
 */
export function isPromoPlan(plan: {
  price?: number;
  regular_price?: number;
  limits?: PlanLimits | null;
}): boolean {
  return plan.limits?.promo === true || precioPromocional(plan) !== null;
}
